import axios, { isAxiosError } from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { ApiResponse } from '@/types/api';
import { isDevMocks } from '@/lib/dev-auth';
import { sanitizeForDemo } from '@/lib/demo-data-sanitizer';
import { getSupabaseClient, isSupabaseLoginEnabled } from '@/lib/supabase';
import { toast } from '@/stores/toast.store';
import { useAuthStore } from '@/stores/auth.store';
import { isPublicAuthPathname } from '@/features/auth/lib/login-path';
import { loginUrlPreservingReturn } from '@/lib/default-landing';

/** Legacy v2 copy when a Base property is at its user seat cap (DEF-281). */
export const PROPERTY_USER_LIMIT_REACHED_MESSAGE =
  'Property has reached the maximum number of users allowed.';

/**
 * Axios instance for the TruSpace Refresh API.
 *
 * Attaches `Authorization: Bearer <accessToken>` on every request. On 401
 * responses it attempts a single transparent refresh (Supabase
 * `refreshSession` when login is enabled; otherwise POST /api/auth/refresh
 * for the non-Supabase/dev path) before retrying the original request.
 */
const defaultHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
};

if (import.meta.env.VITE_API_SECRET) {
  defaultHeaders['x-api-secret'] = import.meta.env.VITE_API_SECRET;
}

export const apiClient = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: defaultHeaders,
});

/**
 * Whether a failed API response should surface the global 5xx toast.
 * Analytics telemetry failures are intentionally silent (DEF-139).
 */
export function shouldShowServerErrorToast(
  status: number | undefined,
  requestUrl: string | undefined,
): boolean {
  if (!status || status < 500) return false;
  const url = requestUrl ?? '';
  if (url.includes('/analytics/events')) return false;
  return true;
}

/**
 * Endpoints a property page may pre-fetch that still 403 for non-Pro tiers
 * (BASE / BASE-Limited). Surfacing those as a "You do not have permission…"
 * toast is spurious — the page hides the tab. Same anti-pattern family as
 * DEF-139: swallow the background failure silently.
 *
 * Matched on a path suffix so `/buildings/:id/stacking-plan` and
 * `/buildings/:id/stacking-plan-detail` are covered. (DEF-40 opened Stacking/
 * VO to company roles on Pro+; these 403s are now tier gates, not SSI-only.)
 */
const SILENT_403_SUFFIXES = [
  '/stacking-plan',
  '/stacking-plan-detail',
  // DEF-39: export route is a distinct suffix (`…/stacking-plan/export`) — endsWith
  // `/stacking-plan` does not match it, so empty-version export 403s were toasting.
  '/stacking-plan/export',
  // DEF-39 / DEF-410: analytics tabs may pre-fetch while Overview viewable is None
  // or still resolving — empty-version 403s are not permission failures.
  '/value-opportunity',
  '/value-opportunity/floors',
  // SSO callback / impersonation handoff own their error UI — global toast is noise.
  '/auth/sso/link',
  '/auth/impersonation/start',
// Login 403s are expected business branches (claim_required, account_disabled,
  // etc.). LoginPage owns the UI — the global permission toast is a nuisance.
  '/auth/login',
  // Messenger JWT is fetched on every authenticated shell load. Roles that
  // Zendesk does not map (or transient 403s) must not spam the permission toast.
  '/support/zendesk/chat-token',
  '/support/zendesk/sso-token',
// Zendesk Messenger handles chat JWT failures internally — global 403 toast is noise
  // and stacks during the Stage chat-token storm (DrawingVaultUser pre-fix).
] as const;

/**
 * Whether a failed API response should surface the global 403 permission toast.
 * SSI-only endpoints that the client pre-fetches in the background are excluded
 * (DEF-152) so their expected 403 doesn't nag non-SSI users.
 */
export function shouldShowPermissionToast(
  status: number | undefined,
  requestUrl: string | undefined,
): boolean {
  if (status !== 403) return false;
  // Strip the query string before matching so `?reportId=…` doesn't defeat the
  // suffix check.
  const path = (requestUrl ?? '').split('?')[0];
  if (SILENT_403_SUFFIXES.some((suffix) => path.endsWith(suffix))) return false;
  return true;
}

/**
 * TEMPORARY (demo): globally silences the generic 5xx "Server error" toast.
 *
 * To RESTORE the toasts, set this back to `false` (or delete this + its use below).
 * A localStorage override wins so it can be toggled live in the browser console during
 * a demo without a rebuild:
 *   localStorage.setItem('ts:suppressServerErrorToasts', '0')  // show toasts again
 *   localStorage.removeItem('ts:suppressServerErrorToasts')    // fall back to the flag
 *
 * Only the anonymous 5xx toast is affected — 403 "no permission" and the session-expired
 * toast still fire.
 */
const DEMO_SUPPRESS_SERVER_ERROR_TOASTS = true;

function serverErrorToastsSuppressed(): boolean {
  try {
    if (typeof window !== 'undefined') {
      const override = window.localStorage.getItem('ts:suppressServerErrorToasts');
      if (override === '1' || override === 'true') return true;
      if (override === '0' || override === 'false') return false;
    }
  } catch {
    // localStorage can throw in privacy modes — fall back to the flag.
  }
  return DEMO_SUPPRESS_SERVER_ERROR_TOASTS;
}

/** Anonymous auth endpoints — never attach a stale Bearer (public token flows). */
const ANONYMOUS_AUTH_PREFIXES = [
  '/auth/login',
  '/auth/internal-domains',
  '/auth/password/forgot',
  '/auth/password/reset',
  '/auth/register',
  // Claim *start* is public; `/auth/claim/complete-name` requires a Bearer (FR-23).
  '/auth/claim/start',
  '/auth/mfa/challenge',
  '/auth/remember-me/resume',
] as const;

function isAnonymousAuthRequest(url: string | undefined): boolean {
  if (!url) return false;
  return ANONYMOUS_AUTH_PREFIXES.some((prefix) => url.startsWith(prefix));
}

/**
 * Bearer-token injector. Reads the current access token from the auth store
 * on every request — the store is the single source of truth. Public routes
 * (e.g. /auth/login, /sso/*) simply have no token to inject, so the header
 * is omitted and the server treats the call as anonymous.
 */
apiClient.interceptors.request.use((config) => {
  const url = config.url ?? '';
  if (isAnonymousAuthRequest(url)) {
    if (config.headers) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (config.headers as any).Authorization;
    }
    return config;
  }
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers = config.headers ?? {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Legacy dev-mock interceptor — opt-in via `VITE_DEV_MOCKS=true` only.
 * `VITE_DEV_AUTH` controls login bypass separately; it does NOT enable mocks.
 * Default local dev proxies every `/api` call to the V3 TS backend (`:4000`).
 */
if (isDevMocks()) {
  const { matchMock } = await import('./dev-mocks');

  apiClient.interceptors.request.use((config) => {
    const url = `${config.baseURL ?? ''}${config.url ?? ''}`;
    const mock = matchMock(url);
    if (mock !== undefined) {
      config.adapter = () =>
        Promise.resolve({
          data: { statusCode: 200, message: 'OK (mock)', result: mock },
          status: 200,
          statusText: 'OK',
          headers: {},
          config,
        });
    }
    return config;
  });
}

// ---------------------------------------------------------------------------
// Refresh-token plumbing: single-flight refresh with request queue.
// ---------------------------------------------------------------------------

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Refresh the access token. When Supabase login is enabled (DEF-418), ask the
 * Supabase client — the real session owner — via `refreshSession`. The legacy
 * POST /api/auth/refresh path remains for non-Supabase/dev auth only; that
 * route is not mounted on the V3 server (`NOT_PORTED`), so using it under
 * Supabase always failed and forced logout at the JWT boundary.
 *
 * Uses a bare axios call (not apiClient) on the legacy path so the request
 * interceptor doesn't attach the expired access token — and so a 401 on the
 * refresh call itself doesn't re-enter this flow.
 */
async function performRefresh(): Promise<string | null> {
  const state = useAuthStore.getState();

  if (isSupabaseLoginEnabled()) {
    try {
      const { data, error } = await getSupabaseClient().auth.refreshSession();
      if (!error && data.session) {
        state.setTokens({
          accessToken: data.session.access_token,
          refreshToken: data.session.refresh_token,
        });
        return data.session.access_token;
      }
      // MFA verify (and a few other paths) may write JWTs to the store without
      // mirroring them into supabase-js first — bridge before giving up (DEF-418).
      const accessToken = state.accessToken;
      const refreshToken = state.refreshToken;
      if (accessToken && refreshToken) {
        const bridged = await getSupabaseClient().auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (!bridged.error && bridged.data.session) {
          state.setTokens({
            accessToken: bridged.data.session.access_token,
            refreshToken: bridged.data.session.refresh_token,
          });
          return bridged.data.session.access_token;
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  const refreshToken = state.refreshToken;
  if (!refreshToken) return null;

  try {
    const res = await axios.post<ApiResponse<{ accessToken: string; refreshToken: string }>>(
      '/api/auth/refresh',
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' }, withCredentials: true },
    );
    const result = res.data?.result ?? (res.data as unknown as { accessToken: string; refreshToken: string });
    if (!result?.accessToken || !result?.refreshToken) {
      return null;
    }
    state.setTokens({
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
    });
    return result.accessToken;
  } catch {
    return null;
  }
}

function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Response interceptor:
 *   1. Normalize API envelope casing.
 *   2. On 401: attempt a single refresh + retry (except for /auth/* calls).
 *   3. Surface 403/5xx toasts.
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const data = response.data;

    // Binary downloads (CSV/Excel/PDF via apiDownload) must pass through raw.
    // The envelope normalizer below treats every object as a JSON envelope, but
    // a Blob/ArrayBuffer is also `typeof 'object'` — wrapping it strips the body
    // so `apiDownload`'s `instanceof Blob` check fails and no file is saved (DEF-28).
    const responseType = response.config?.responseType;
    if (responseType === 'blob' || responseType === 'arraybuffer' || data instanceof Blob) {
      return response;
    }

    if (data === null) {
      return {
        ...response,
        data: { statusCode: response.status, message: '', result: null },
      };
    }

    if (typeof data !== 'object') {
      return response;
    }

    const record = data as Record<string, unknown>;
    const normalized: ApiResponse = {
      statusCode: (record.statusCode ?? record.StatusCode ?? response.status) as number,
      message: String(record.message ?? record.Message ?? ''),
      result: sanitizeForDemo(
        (record.result ?? record.Result ?? data) as ApiResponse['result'],
      ),
    };

    return { ...response, data: normalized };
  },
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    const url = original?.url ?? '';

    // Skip refresh flow for auth endpoints themselves — they shouldn't loop.
    const isAuthCall =
      isAnonymousAuthRequest(url) ||
      url.startsWith('/auth/refresh') ||
      url.startsWith('/auth/logout') ||
      url.startsWith('/auth/sso/link') ||
      url.startsWith('/sso/');

    if (status === 401 && original && !original._retry && !isAuthCall) {
      original._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers = original.headers ?? {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (original.headers as any).Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }
      // Refresh failed — clear session, surface a "session expired"
      // toast per PRD §11/05 (re-prompt requirement), then bounce to
      // login. The toast fires *before* the redirect so it survives
      // the route change via the toast store's React tree.
      //
      // `wasAuthenticated` is snapshotted BEFORE `logout()` so the
      // toast only fires for users who had an active session at the
      // moment of the 401. Anonymous callers hitting a protected
      // route never see it.
      // The SSO callback route owns its own session lifecycle: supabase-js is
      // mid-flight exchanging the OAuth `?code=` for a session. A stray 401
      // from a background query must NOT logout/redirect here or it destroys
      // the page before the exchange completes.
      const onSsoCallback =
        typeof window !== 'undefined' && window.location.pathname.startsWith('/v2/auth/sso/callback');
      if (onSsoCallback) {
        return Promise.reject(error);
      }
      const onPublicAuthPage =
        typeof window !== 'undefined' && isPublicAuthPathname();
      if (onPublicAuthPage) {
        return Promise.reject(error);
      }
      const authStore = useAuthStore.getState();
      const wasAuthenticated = authStore.isAuthenticated;
      authStore.logout();
      if (typeof window !== 'undefined' && !isPublicAuthPathname()) {
        if (wasAuthenticated) {
          toast.info('Your session has expired. Please sign in again.');
        }
        const browserPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.href = loginUrlPreservingReturn(browserPath);
      }
      return Promise.reject(error);
    }

    if (status === 401 && isAuthCall) {
      // Login/refresh failed; don't attempt recovery, let callers surface it.
      return Promise.reject(error);
    }

    if (status === 403) {
      // DEF-323: a caller-opted-in silent lookup (e.g. the Create Order property
      // prefill, which soft-fails to manual entry on a BASE-LIMITED building) must
      // never surface the generic permission toast — same anti-pattern family as
      // `SILENT_403_SUFFIXES`, but per-call instead of per-path since this endpoint
      // (`/portfolio/building/:id`) is also used where a 403 IS meaningful.
      const suppressed = Boolean(
        (error.config as (AxiosRequestConfig & { _suppressPermissionToast?: boolean }) | undefined)
          ?._suppressPermissionToast,
      );
      if (!suppressed && shouldShowPermissionToast(status, error.config?.url)) {
        toast.error('You do not have permission to perform this action.');
      }
    } else if (
      shouldShowServerErrorToast(status, error.config?.url) &&
      !serverErrorToastsSuppressed()
    ) {
      toast.error('Server error. Please try again later.');
    }

    return Promise.reject(error);
  },
);

/** Per-request opt-out of the global 403 "no permission" toast (DEF-323). */
export type SilentPermissionRequestConfig = AxiosRequestConfig & {
  _suppressPermissionToast?: boolean;
};

/**
 * Type-safe API request helpers that unwrap the normalized response.
 */
export async function apiGet<T>(
  url: string,
  params?: Record<string, unknown>,
  config?: SilentPermissionRequestConfig,
): Promise<T> {
  const response = await apiClient.get<ApiResponse<T>>(url, { ...config, params });
  return response.data.result;
}

export async function apiPost<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.post<ApiResponse<T>>(url, data, config);
  return response.data.result;
}

export async function apiPut<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.put<ApiResponse<T>>(url, data, config);
  return response.data.result;
}

export async function apiPatch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.patch<ApiResponse<T>>(url, data, config);
  return response.data.result;
}

export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.delete<ApiResponse<T>>(url, config);
  return response.data.result;
}

/**
 * Extracts a user-facing message from a failed API request. Prefers the V3
 * `{ error: { message, details? } }` body over axios' generic status-line text.
 * When Zod fieldErrors/formErrors are present, prefers the first specific message so
 * callers see e.g. phone/name validation copy instead of the generic envelope text
 * (`ValidationError`'s constructed "Invalid request body" — DEF-196).
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as {
      error?: {
        message?: string;
        details?: {
          limit?: string;
          fieldErrors?: Record<string, string[] | undefined>;
          formErrors?: string[];
        };
      };
    } | undefined;
    if (error.response?.status === 402 && body?.error?.details?.limit === 'max_users') {
      return PROPERTY_USER_LIMIT_REACHED_MESSAGE;
    }
    if (body?.error?.message === PROPERTY_USER_LIMIT_REACHED_MESSAGE) {
      return PROPERTY_USER_LIMIT_REACHED_MESSAGE;
    }
    const fieldErrors = body?.error?.details?.fieldErrors;
    if (fieldErrors) {
      for (const messages of Object.values(fieldErrors)) {
        const first = messages?.[0];
        if (first) return first;
      }
    }
    const formErrors = body?.error?.details?.formErrors;
    if (formErrors?.[0]) return formErrors[0];
    if (body?.error?.message) return body.error.message;
  }
  if (error instanceof Error && error.message.trim().length > 0) return error.message;
  return fallback;
}

/**
 * POST a multipart form (file uploads). Do not set Content-Type manually —
 * the client default is application/json and a bare multipart/form-data header
 * omits the boundary, which makes multer reject the body.
 */
export async function apiPostMultipart<T>(
  url: string,
  formData: FormData,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.post<ApiResponse<T>>(url, formData, {
    ...config,
    headers: { ...config?.headers, 'Content-Type': undefined },
  });
  return response.data.result;
}

/**
 * Download a file (Excel, PDF, CSV) as a blob.
 */
export async function apiDownload(url: string, filename: string): Promise<void> {
  const response = await apiClient.get(url, { responseType: 'blob' });
  const data = response.data;
  if (!(data instanceof Blob)) {
    throw new Error('Download did not return a file');
  }
  const contentType = data.type.toLowerCase();
  if (contentType.includes('json') || contentType.includes('html')) {
    const text = await data.text();
    throw new Error(text.slice(0, 500));
  }
  const blobUrl = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(blobUrl);
}

/**
 * GET a plain-text response (CSV chunks, etc.) without the JSON result envelope.
 */
export async function apiFetchText(
  url: string,
  params?: Record<string, unknown>,
): Promise<string> {
  const { text } = await apiFetchTextWithMeta(url, params);
  return text;
}

/** Like `apiFetchText`, but exposes response headers (export cursors, row counts). */
export async function apiFetchTextWithMeta(
  url: string,
  params?: Record<string, unknown>,
): Promise<{ text: string; headers: Record<string, string> }> {
  const response = await apiClient.get<string>(url, {
    params,
    responseType: 'text',
    transformResponse: [(data) => data],
  });
  const contentType = String(response.headers['content-type'] ?? '').toLowerCase();
  if (contentType.includes('json')) {
    throw new Error(String(response.data).slice(0, 500));
  }

  const headers: Record<string, string> = {};
  for (const [key, value] of Object.entries(response.headers)) {
    if (typeof value === 'string') {
      headers[key.toLowerCase()] = value;
    }
  }

  return { text: response.data, headers };
}
